export class BackendClient {
    baseUrl;
    accessToken = null;
    constructor(baseUrl) {
        this.baseUrl = baseUrl;
    }
    setAccessToken(t) {
        this.accessToken = t;
    }
    headers(auth = true) {
        const h = { 'Content-Type': 'application/json' };
        if (auth && this.accessToken) {
            h['Authorization'] = `Bearer ${this.accessToken}`;
        }
        return h;
    }
    async login(email, password) {
        const r = await fetch(`${this.baseUrl}/auth/login`, {
            method: 'POST',
            headers: this.headers(false),
            body: JSON.stringify({ email, password }),
        });
        if (!r.ok) {
            throw new Error(`login failed: ${r.status} ${await r.text()}`);
        }
        return r.json();
    }
    async listDevices() {
        const r = await fetch(`${this.baseUrl}/devices`, {
            headers: this.headers(),
        });
        if (!r.ok) {
            throw new Error(`list devices failed: ${r.status}`);
        }
        return r.json();
    }
    /**
     * Faz U8.13: Backend'in birleştirdiği adres defteri —
     * self-owned + granted + manual entries tek listede.
     */
    async getAddressBook() {
        const r = await fetch(`${this.baseUrl}/address-book`, {
            headers: this.headers(),
        });
        if (!r.ok)
            throw new Error(`address-book list failed: ${r.status}`);
        return r.json();
    }
    async createAddressBookEntry(body) {
        const r = await fetch(`${this.baseUrl}/address-book`, {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify(body),
        });
        if (!r.ok)
            throw new Error(`create entry failed: ${r.status} ${await r.text()}`);
        return r.json();
    }
    async deleteAddressBookEntry(id) {
        const r = await fetch(`${this.baseUrl}/address-book/${id}`, {
            method: 'DELETE',
            headers: this.headers(),
        });
        if (!r.ok && r.status !== 404) {
            throw new Error(`delete entry failed: ${r.status}`);
        }
    }
    /**
     * Faz U8.8: Kullanıcının LastSeenAt'i eski (default 30 gün) host
     * cihazlarını toplu soft-revoke eder. Hard-delete değil — audit
     * chain korunur, aynı PC yeniden açılırsa yeniden register olur.
     */
    async cleanupStaleDevices(dayThreshold = 30) {
        const r = await fetch(`${this.baseUrl}/devices/cleanup-stale`, {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify({ dayThreshold }),
        });
        if (!r.ok) {
            throw new Error(`cleanup-stale failed: ${r.status} ${await r.text()}`);
        }
        return r.json();
    }
    async consumePairing(hostHumanId, code, controllerTransport = 'wss') {
        const r = await fetch(`${this.baseUrl}/pairing`, {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify({ hostHumanId, code, controllerTransport }),
        });
        if (!r.ok) {
            throw new Error(`pairing failed: ${r.status} ${await r.text()}`);
        }
        return r.json();
    }
    async consumeUnattended(token, controllerTransport = 'wss') {
        const r = await fetch(`${this.baseUrl}/unattended`, {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify({ token, controllerTransport }),
        });
        if (!r.ok) {
            throw new Error(`unattended failed: ${r.status} ${await r.text()}`);
        }
        return r.json();
    }
    /**
     * Faz U/V: Aynı hesap kendi cihazına (SelfOwned) veya persistent grant
     * sahibi başka kullanıcının cihazına pairing kodu olmadan bağlanır.
     * Response ConsumePairingResponse ile aynı şekil (sessionId, deviceId,
     * relaySessionId, forceRelay) — session.ts aynı handler'ı kullanır.
     */
    async connectAuthorize(deviceId, controllerTransport = 'wss') {
        const r = await fetch(`${this.baseUrl}/connect/authorize`, {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify({ deviceId, controllerTransport }),
        });
        if (!r.ok) {
            const body = await r.text();
            if (r.status === 403) {
                throw new Error('no_authorization: Bu cihaza erişim izniniz yok. ' +
                    'Pairing kodu gir veya sahip kişiden kalıcı izin iste.');
            }
            throw new Error(`connect-authorize failed: ${r.status} ${body}`);
        }
        const resp = await r.json();
        // ConsumePairingResponse shape'e dönüştür (hostHumanId alias).
        return {
            sessionId: resp.sessionId,
            deviceId: resp.deviceId,
            hostHumanId: resp.deviceHumanId,
            relaySessionId: resp.relaySessionId,
            forceRelay: resp.forceRelay,
        };
    }
    // ===== Faz 4-20: WebAuthn endpoint'leri =====
    async webauthnRegisterBegin() {
        const r = await fetch(`${this.baseUrl}/auth/webauthn/credentials/begin`, {
            method: 'POST',
            headers: this.headers(),
        });
        if (!r.ok) {
            throw new Error(`webauthn register/begin failed: ${r.status} ${await r.text()}`);
        }
        return r.json();
    }
    async webauthnRegisterFinish(originalOptions, attestationResponse, label) {
        const r = await fetch(`${this.baseUrl}/auth/webauthn/credentials/finish`, {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify({
                originalOptions,
                attestationResponse,
                label,
            }),
        });
        if (!r.ok) {
            throw new Error(`webauthn register/finish failed: ${r.status} ${await r.text()}`);
        }
        return r.json();
    }
    async webauthnListCredentials() {
        const r = await fetch(`${this.baseUrl}/auth/webauthn/credentials`, {
            headers: this.headers(),
        });
        if (!r.ok) {
            throw new Error(`webauthn list failed: ${r.status}`);
        }
        return r.json();
    }
    async webauthnDeleteCredential(id) {
        const r = await fetch(`${this.baseUrl}/auth/webauthn/credentials/${id}`, {
            method: 'DELETE',
            headers: this.headers(),
        });
        if (!r.ok && r.status !== 204) {
            throw new Error(`webauthn delete failed: ${r.status}`);
        }
    }
    async webauthnLoginBegin(email) {
        const r = await fetch(`${this.baseUrl}/auth/webauthn/login/begin`, {
            method: 'POST',
            headers: this.headers(false),
            body: JSON.stringify({ email }),
        });
        if (!r.ok) {
            throw new Error(`webauthn login/begin failed: ${r.status} ${await r.text()}`);
        }
        return r.json();
    }
    async webauthnLoginFinish(originalOptions, assertionResponse) {
        const r = await fetch(`${this.baseUrl}/auth/webauthn/login/finish`, {
            method: 'POST',
            headers: this.headers(false),
            body: JSON.stringify({ originalOptions, assertionResponse }),
        });
        if (!r.ok) {
            throw new Error(`webauthn login/finish failed: ${r.status} ${await r.text()}`);
        }
        return r.json();
    }
    // Faz 4-79: SSO provider list + password reset request.
    async listSsoProviders() {
        const r = await fetch(`${this.baseUrl}/auth/sso/providers`, {
            headers: this.headers(false),
        });
        if (!r.ok)
            return [];
        return r.json();
    }
    async requestPasswordReset(email) {
        const r = await fetch(`${this.baseUrl}/auth/password-reset/request`, {
            method: 'POST',
            headers: this.headers(false),
            body: JSON.stringify({ email }),
        });
        if (!r.ok) {
            throw new Error(`reset request failed: ${r.status}`);
        }
    }
    // ============ Admin: Tenant policy ============
    async getAdminPolicy() {
        const r = await fetch(`${this.baseUrl}/admin/policy`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`admin/policy: ${r.status}`);
        return r.json();
    }
    async putAdminPolicy(p) {
        const r = await fetch(`${this.baseUrl}/admin/policy`, {
            method: 'PUT', headers: this.headers(), body: JSON.stringify(p),
        });
        if (!r.ok)
            throw new Error(`admin/policy PUT: ${r.status}`);
        return r.json();
    }
    // ============ Admin: Devices / Sessions / Audit ============
    async listAdminDevices(tag, limit = 200) {
        const q = new URLSearchParams({ limit: String(limit) });
        if (tag)
            q.set('tag', tag);
        const r = await fetch(`${this.baseUrl}/admin/devices?${q}`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`admin/devices: ${r.status}`);
        return r.json();
    }
    async listAdminSessions(activeOnly = false, limit = 200) {
        const q = new URLSearchParams({ limit: String(limit) });
        if (activeOnly)
            q.set('activeOnly', 'true');
        const r = await fetch(`${this.baseUrl}/admin/sessions?${q}`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`admin/sessions: ${r.status}`);
        return r.json();
    }
    async listAdminAudit(limit = 100, eventType) {
        const q = new URLSearchParams({ limit: String(limit) });
        if (eventType)
            q.set('eventType', eventType);
        const r = await fetch(`${this.baseUrl}/admin/audit?${q}`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`admin/audit: ${r.status}`);
        return r.json();
    }
    async exportAuditCsv(limit = 10000, eventType, since) {
        const q = new URLSearchParams({ limit: String(limit) });
        if (eventType)
            q.set('eventType', eventType);
        if (since)
            q.set('since', since);
        const r = await fetch(`${this.baseUrl}/admin/audit/export.csv?${q}`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`audit export: ${r.status}`);
        return r.blob();
    }
    async disableDevice(id) {
        const r = await fetch(`${this.baseUrl}/admin/devices/${id}/disable`, {
            method: 'POST', headers: this.headers(),
        });
        if (!r.ok)
            throw new Error(`disable device: ${r.status}`);
    }
    async endSession(id) {
        const r = await fetch(`${this.baseUrl}/admin/sessions/${id}/end`, {
            method: 'POST', headers: this.headers(),
        });
        if (!r.ok)
            throw new Error(`end session: ${r.status}`);
    }
    async disableUser(id) {
        const r = await fetch(`${this.baseUrl}/admin/users/${id}/disable`, {
            method: 'POST', headers: this.headers(),
        });
        if (!r.ok)
            throw new Error(`disable user: ${r.status}`);
    }
    async setDeviceTags(id, tags) {
        const r = await fetch(`${this.baseUrl}/admin/devices/${id}/tags`, {
            method: 'PUT', headers: this.headers(), body: JSON.stringify({ tags }),
        });
        if (!r.ok)
            throw new Error(`device tags: ${r.status}`);
    }
    // ============ Admin: Webhooks ============
    async listWebhooks() {
        const r = await fetch(`${this.baseUrl}/admin/webhooks`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`webhooks: ${r.status}`);
        return r.json();
    }
    async createWebhook(url, events, label) {
        const r = await fetch(`${this.baseUrl}/admin/webhooks`, {
            method: 'POST', headers: this.headers(),
            body: JSON.stringify({ url, events, label }),
        });
        if (!r.ok)
            throw new Error(`create webhook: ${r.status} ${await r.text()}`);
        return r.json();
    }
    async deleteWebhook(id) {
        const r = await fetch(`${this.baseUrl}/admin/webhooks/${id}`, {
            method: 'DELETE', headers: this.headers(),
        });
        if (!r.ok && r.status !== 204)
            throw new Error(`delete webhook: ${r.status}`);
    }
    async testWebhook(id) {
        const r = await fetch(`${this.baseUrl}/admin/webhooks/${id}/test`, {
            method: 'POST', headers: this.headers(),
        });
        if (!r.ok)
            throw new Error(`test webhook: ${r.status}`);
        return r.json();
    }
    async listWebhookEvents() {
        const r = await fetch(`${this.baseUrl}/admin/webhooks/events`, { headers: this.headers() });
        if (!r.ok)
            return [];
        return r.json();
    }
    // ============ Admin: Feature flags + Policy versions ============
    async getFeatureFlags() {
        const r = await fetch(`${this.baseUrl}/admin/feature-flags`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`feature-flags: ${r.status}`);
        return r.json();
    }
    async listPolicyVersions(limit = 50) {
        const r = await fetch(`${this.baseUrl}/admin/policy/versions?limit=${limit}`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`policy versions: ${r.status}`);
        return r.json();
    }
    async getPolicyVersion(id) {
        const r = await fetch(`${this.baseUrl}/admin/policy/versions/${id}`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`policy version: ${r.status}`);
        return r.json();
    }
    // ============ Device monitors ============
    async getDeviceMonitors(id) {
        const r = await fetch(`${this.baseUrl}/devices/${id}/monitors`, { headers: this.headers() });
        if (!r.ok)
            return [];
        return r.json();
    }
    // ============ Faz 9: Chat ============
    async chatListThreads() {
        const r = await fetch(`${this.baseUrl}/chat/threads`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`chat threads: ${r.status}`);
        return r.json();
    }
    async chatCreateThread(peerEmail) {
        const r = await fetch(`${this.baseUrl}/chat/threads`, {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify({ peerEmail }),
        });
        if (!r.ok)
            throw new Error(`chat create: ${r.status} ${await r.text()}`);
        return r.json();
    }
    async chatGetMessages(threadId, since) {
        const url = since
            ? `${this.baseUrl}/chat/threads/${threadId}/messages?since=${encodeURIComponent(since)}`
            : `${this.baseUrl}/chat/threads/${threadId}/messages`;
        const r = await fetch(url, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`chat msgs: ${r.status}`);
        return r.json();
    }
    async chatSendMessage(threadId, body) {
        const r = await fetch(`${this.baseUrl}/chat/threads/${threadId}/messages`, {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify({ body }),
        });
        if (!r.ok)
            throw new Error(`chat send: ${r.status} ${await r.text()}`);
        return r.json();
    }
    // ============ Faz 9: Meetings ============
    async meetingsList() {
        const r = await fetch(`${this.baseUrl}/meetings`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`meetings list: ${r.status}`);
        return r.json();
    }
    async meetingsCreate(title, inviteEmails) {
        const r = await fetch(`${this.baseUrl}/meetings`, {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify({ title, inviteEmails }),
        });
        if (!r.ok)
            throw new Error(`meetings create: ${r.status} ${await r.text()}`);
        return r.json();
    }
    async meetingGet(id) {
        const r = await fetch(`${this.baseUrl}/meetings/${id}`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`meetings get: ${r.status}`);
        return r.json();
    }
    async meetingInvite(id, email) {
        const r = await fetch(`${this.baseUrl}/meetings/${id}/invite`, {
            method: 'POST', headers: this.headers(),
            body: JSON.stringify({ email }),
        });
        if (!r.ok)
            throw new Error(`meetings invite: ${r.status} ${await r.text()}`);
        return r.json();
    }
    async meetingJoin(id) {
        const r = await fetch(`${this.baseUrl}/meetings/${id}/join`, {
            method: 'POST', headers: this.headers(),
        });
        if (!r.ok)
            throw new Error(`meetings join: ${r.status} ${await r.text()}`);
        return r.json();
    }
    async meetingLeave(id) {
        await fetch(`${this.baseUrl}/meetings/${id}/leave`, {
            method: 'POST', headers: this.headers(),
        });
    }
    async meetingEnd(id) {
        await fetch(`${this.baseUrl}/meetings/${id}/end`, {
            method: 'POST', headers: this.headers(),
        });
    }
}
// Yukarıdaki interface'ler export edilir, metodlar BackendClient class'ında.
